import _ from 'lodash'
import { withCompInfo } from '@wix/editor-elements-integrations'
import { color as colorUtils, links, text as textUtils } from '@wix/thunderbolt-commons'
import {
	correctInlineColorStyle,
	correctInlineFontStyle,
	correctInlineFontStyleDesktop,
	getMobileScaledFont,
	parseFontStr,
	getScaledFontSize,
} from '@wix/thunderbolt-becky-root'
import type { CompInfo } from '@wix/editor-elements-types/thunderbolt'
import type {
	IntermediateLink,
	LinkProps,
	SingleLayoutData,
	Spx,
	WRichTextA11y,
	WRichTextDefinition,
} from '@wix/thunderbolt-becky-types'
import type { WRichTextCSSVars, WRichTextProps } from './WRichText'
import { WRichTextSelectorCSSVars } from './WRichText'
import { getHeightInPixels, getWidthInPixels } from '@wix/editor-elements-common-utils'

const { hasVerticalTextData, unwrapVerticalText, wrapVerticalText, dataFixVerticalText } = textUtils
const { convertDataQueryLinksToHtmlAnchors, isLinkData } = links
const { getFromColorMap, extractThemeColor, isThemeColor } = colorUtils

export const RESPONSIVE_TAGS = ':is(p,h1,h2,h3,h4,h5,h6,ul,ol,span[data-attr-richtext-marker],blockquote,div)' as const
const INTERACTION_TAGS = ':is(p,h1,h2,h3,h4,h5,h6,ul,ol,span,blockquote,div)' as const

const RICH_TEXT_SELECTOR = '[class$=rich-text__text]'
const CORVID_COLOR_VAR = '--corvid-color'

const isStretchedAndResponsive = (
	compLayout: CompInfo['compLayout'],
	experiments: CompInfo['experiments'],
	isOneDocMigrated: boolean,
	compSingleLayout?: SingleLayoutData
) => {
	const responsiveTextInStretched = experiments['specs.thunderbolt.responsiveTextInStretched']
	const isStretched = isOneDocMigrated
		? compSingleLayout?.itemLayout?.type === 'MeshItemLayout' &&
		compSingleLayout?.itemLayout?.justifySelf === 'stretch'
		: !!(compLayout?.docked?.left && compLayout?.docked?.right)
	return isStretched && responsiveTextInStretched
}

export const css = withCompInfo<WRichTextCSSVars, any>()(
	[
		'compProps',
		'styleProperties',
		'compLayout',
		'compSingleLayout',
		'isMobileView',
		'hasResponsiveLayout',
		'siteFontsSpxResolved',
		'isOneDocMigrated',
		'classicRotation',
	],
	({
		compProps,
		styleProperties,
		compLayout,
		compSingleLayout,
		isMobileView,
		hasResponsiveLayout,
		siteFontsSpxResolved,
		isOneDocMigrated,
		classicRotation,
	}) => {
		const { packed, verticalText, minHeight, minWidth } = compProps
		const scale = compProps.fontScale ?? isOneDocMigrated ? 1 : compLayout.scale
		const isPacked = packed || isMobileView

		const width = isOneDocMigrated ? getWidthInPixels(compSingleLayout) : compLayout.width
		const height = isOneDocMigrated ? getHeightInPixels(compSingleLayout) : compLayout.height
		const rotationInDegrees = isOneDocMigrated ? classicRotation : compLayout.rotationInDegrees

		// Use resolved spx font map whenever applicable
		const fontMap = siteFontsSpxResolved

		/*
		 ** TODO: Parse html from compData and get only used fonts/colors
		 */
		const scaledFonts =
			scale !== 1
				? _.chain(fontMap)
					.mapValues((font: string, _key: string) => {
						return isMobileView ? getMobileScaledFont(font, scale!) : font
					})
					.mapKeys((_value, key) => `--${key}`)
					.value()
				: {}

		const resolveMinSize = (minSizeKey: number, sizeKey: number) =>
			isPacked ? minSizeKey : isMobileView ? sizeKey : minSizeKey || sizeKey

		/**
	 The minSize value is calculated by the following conditions (the example is for height, but same for width):
			if packed & mobile => 'min-height': 0
			if packed & !mobile => 'min-height': minHeight
			if !packed & mobile => 'min-height': height
			if !packed & !mobile => 'min-height': minHeight || height
	 */
		const minSizeCssValue =
			!rotationInDegrees &&
			!hasResponsiveLayout &&
			!(isPacked && isMobileView) &&
			resolveMinSize(verticalText ? minWidth : minHeight, (verticalText ? width : height)!)

		const minSizeCss = !minSizeCssValue
			? {}
			: verticalText
				? { 'min-width': `${minSizeCssValue}px` }
				: { '--min-height': `${minSizeCssValue}px` }

		const siteDirection = 'var(--wix-opt-in-direction)'
		const textDirection = { '--text-direction': styleProperties.direction || siteDirection }

		const writingMode = styleProperties.writingMode ? { 'writing-mode': styleProperties.writingMode } : {}
		const textOrientation = styleProperties.textOrientation
			? { 'text-orientation': styleProperties.textOrientation }
			: {}

		return {
			...scaledFonts,
			...minSizeCss,
			...textDirection,
			...writingMode,
			...textOrientation,
		}
	}
)

const getFontClass = (className: string) => {
	const findFontClass = /(font_\d+)/
	const fontClass = findFontClass.exec(className)
	return fontClass?.[1] || ''
}

const resolveColor = (color: string, siteColors: CompInfo['siteColors']) => {
	const parsedColor = extractThemeColor(color || 'none')
	return isThemeColor(parsedColor) ? getFromColorMap(color, siteColors) : color
}

const extractFontSize = (font: string) => {
	const fontObj = parseFontStr(font)
	const { size, sizeUnit } = fontObj
	return sizeUnit && sizeUnit === 'spx' ? `calc(${size} * var(--theme-spx-ratio))` : font
}

const getFontSize = (
	experiments: CompInfo['experiments'],
	compData: CompInfo['compData'],
	styleProperties: CompInfo['styleProperties'],
	formatCssValue: CompInfo['formatCssValue'],
	siteFonts: CompInfo['siteFonts']
) => {
	const { text } = compData
	const { spx, fontSize: fontSizeOverride, minFontSize, maxFontSize } = styleProperties

	const textFontClass = getFontClass(text)
	const shouldFormatFontSizeOverride = fontSizeOverride && spx
	const formattedFontSizeOverride = shouldFormatFontSizeOverride
		? formatCssValue(fontSizeOverride, (spx as unknown) as Spx, true)
		: fontSizeOverride
	const fontSize = fontSizeOverride
		? formattedFontSizeOverride
		: siteFonts[textFontClass]
			? extractFontSize(siteFonts[textFontClass])
			: siteFonts[textFontClass]
	const fontSizeWithMinOnly = `max(${minFontSize}, ${fontSize})`
	const fontSizeWithMaxOnly = `min(${maxFontSize}, ${fontSize})`
	const fontSizeWithMinAndMax = `max(${minFontSize}, min(${maxFontSize}, ${fontSize}))`

	const fallbackValue = fontSizeOverride ? { value: formattedFontSizeOverride } : {}
	const fontSizeWithoutMin = maxFontSize ? fontSizeWithMaxOnly : fallbackValue.value
	const minMaxFontSize =
		minFontSize && maxFontSize ? fontSizeWithMinAndMax : minFontSize ? fontSizeWithMinOnly : fontSizeWithoutMin

	// min/max font-size doesn't make sense if font-size is defined in absolute length(for example px)
	return fontSize && fontSize.endsWith('px') ? fallbackValue.value : minMaxFontSize
}

export const selectorToCss = withCompInfo<WRichTextSelectorCSSVars, any>()(
	[
		'compProps',
		'compLayout',
		'hasResponsiveLayout',
		'experiments',
		'styleProperties',
		'compData',
		'formatCssValue',
		'siteFonts',
		'siteColors',
		// @ts-expect-error - Remove once isResponsive is added to the compInfo
		'isResponsive',
		// @ts-expect-error - Remove once isDefaultVariant is added to the compInfo
		'isDefaultVariant',
		'compSingleLayout',
		'isOneDocMigrated',
	],
	({
		compProps,
		hasResponsiveLayout,
		compLayout,
		experiments,
		styleProperties,
		compData,
		formatCssValue,
		siteFonts,
		siteColors,
		// @ts-expect-error - Remove once isResponsive is added to the compInfo
		isResponsive,
		// @ts-expect-error - Remove once isDefaultVariant is added to the compInfo
		isDefaultVariant,
		compSingleLayout,
		isOneDocMigrated,
	}) => {
		const {
			color,
			backgroundColor,
			textScale__maxFontSize,
			textScale__minFontSize,
			lineHeight,
			fontFamily,
			fontWeight,
			fontStyle,
			textDecoration,
			textAlign,
			letterSpacing,
			writingMode,
		} = styleProperties

		const scaleFontSize = getScaledFontSize(textScale__minFontSize, textScale__maxFontSize)
		const fontSize = getFontSize(experiments, compData, styleProperties, formatCssValue, siteFonts)

		const hasTextScale = textScale__maxFontSize && textScale__minFontSize
		const fontSizeResult = hasTextScale ? scaleFontSize : fontSize

		/**
		 * isResponsiveLayoutInClassicSite will be true in case of a WRichText component inside a Blocks widget.
		 * This Code is a temp workaround for https://jira.wixpress.com/browse/EE-36819
		 */
		const isResponsiveLayoutInClassicSite = hasResponsiveLayout && !isResponsive

		const isNotDefaultStyle = !isDefaultVariant
		const responsiveLineHeight =
			_.isString(lineHeight) && lineHeight.endsWith('em')
				? lineHeight.substring(0, lineHeight.length - 2)
				: lineHeight

		const isTextAlign = (key: string) => key === 'text-align'
		const responsiveStyles = _({
			color: resolveColor(color, siteColors),
			'background-color': resolveColor(backgroundColor, siteColors),
			'font-family': fontFamily,
			'font-weight': fontWeight,
			'font-size': fontSizeResult,
			'font-style': fontStyle,
			'text-decoration': textDecoration,
			'text-align': textAlign,
			'letter-spacing': letterSpacing,
			'line-height': responsiveLineHeight,
			'min-height': hasTextScale ? '0vw' : '', // fixes safari not recalculating the calc bug in text scale
			'writing-mode': writingMode,
		})
			.pickBy((style) => style)
			.mapValues((style, key) =>
				(!isTextAlign(key) && isNotDefaultStyle) || isResponsiveLayoutInClassicSite
					? `${style} !important`
					: style
			) // We need to add !important because we need to override inline style
			.value()

		const result: Record<string, object> = {}

		if (
			hasResponsiveLayout ||
			isStretchedAndResponsive(compLayout, experiments, isOneDocMigrated, compSingleLayout)
		) {
			result[RESPONSIVE_TAGS] = responsiveStyles
		} else if (isNotDefaultStyle) {
			result[INTERACTION_TAGS] = responsiveStyles
		}

		const { overrideAlignment } = compProps
		if (overrideAlignment) {
			result['*'] = { 'text-align': `${overrideAlignment} !important` }
		}

		if (responsiveStyles.color) {
			result[RICH_TEXT_SELECTOR + RESPONSIVE_TAGS] = {
				color: `var(${CORVID_COLOR_VAR}, ${responsiveStyles.color})`,
			}
		}

		return result
	}
)

// Important! If you add code to this Props mapper, make sure you add it also to WRichText.carmi if exists
export const props = withCompInfo<WRichTextProps, WRichTextDefinition>()(
	[
		'compData',
		'compProps',
		'skin',
		'compLayout',
		'isMobileView',
		'siteColors',
		'siteFonts',
		'hasResponsiveLayout',
		'isOneDocMigrated',
	],
	({
		compData,
		compProps,
		skin,
		compLayout,
		isMobileView,
		siteColors,
		siteFonts,
		hasResponsiveLayout,
		isOneDocMigrated,
	}) => {
		const { a11y, linkList, text } = compData
		const { overrideColor, brightness, fontScale, verticalText } = compProps

		let html = wrapVerticalTextIfNeeded({ verticalText, text })

		// Add anchors to the html
		if (linkList?.length > 0) {
			const linkProps = (linkList as Array<IntermediateLink>)
				.filter(isLinkData)
				.reduce<Record<string, LinkProps>>((acc, linkData) => {
					const relevantLinkData = _.omit(linkData, 'docInfo', 'id', 'type') // filter out docInfo/type from resolvedLink
					return Object.assign(acc, { [linkData.id]: relevantLinkData })
				}, {})

			html = convertDataQueryLinksToHtmlAnchors(html, linkProps)
		}

		// Correct font size and color in mobile
		if (isMobileView) {
			const scale = fontScale ?? (isOneDocMigrated ? 1 : compLayout.scale)
			if (scale) {
				html = correctInlineFontStyle(html, scale, siteFonts)
			}

			if (overrideColor || (typeof brightness !== 'undefined' && brightness !== 1)) {
				html = correctInlineColorStyle(html, overrideColor!, brightness, siteColors, siteFonts)
			}
		}

		// Correct inline fonts for non responsive text
		if (!isMobileView && !hasResponsiveLayout) {
			html = correctInlineFontStyleDesktop(html, siteFonts)
		}

		return {
			html,
			skin,
			...(a11y && { a11y: getStyledTextA11yProps(a11y) }),
		}
	}
)

const wrapVerticalTextIfNeeded = ({ verticalText, text }: { verticalText: boolean; text: string }): string => {
	if (verticalText) {
		if (!hasVerticalTextData(text)) {
			return wrapVerticalText(text)
		}

		/**
		 * This is kind of data-fixer for sites that have vertical text html saved already
		 * in compData with vertical text wrapper that has width: 100%. It had to be removed
		 * because it caused a bug in Safari browser 17 version for vertical text to shift to
		 * certain side.
		 * https://jira.wixpress.com/browse/ECL-9385
		 */
		return dataFixVerticalText(text)
	}

	if (!hasVerticalTextData(text)) {
		return text
	}

	return unwrapVerticalText(text)
}

const getStyledTextA11yProps = (a11y: WRichTextA11y): WRichTextProps['a11y'] => {
	const { label, live, level, role, atomic, hidden, current } = a11y
	return {
		'aria-label': label,
		'aria-live': live,
		'aria-level': level,
		'aria-current': current,
		...(atomic !== undefined && { 'aria-atomic': `${atomic}` }),
		...(hidden !== undefined && { 'aria-hidden': `${hidden}` }),
		role,
	}
}
